// @ts-nocheck

import type {Action} from 'src/action-creators/draft-messages';
import type {SuggestionType} from 'src/types/contacts';
import type {
  MessagingDraft,
  SetOfDiscoverCandidateDetails,
  NumbersWithNames,
  MsgExtLocationDetails,
} from 'src/types/draft-messages';
import type {
  BroadcastSuppressionPhoneDetails,
  TimeFrame,
} from 'src/components/messaging/broadcast-suppression/types';

import omit from 'lodash/omit';
import uniqueId from 'lodash/uniqueId';

import {
  SET_GRABBED_PHONE_NUMBERS,
  INPUT_PHONE,
  SET_MULTIPLE_RECIPIENTS,
  SET_DISCOVER_OR_TRM_ORIGIN_AND_METADATA,
  SET_DISCOVER_OR_TRM_IFRAME_CONTEXT,
  CHANGE_DRAFT,
  CLEAR_DRAFT,
  ADD_SCHEDULE,
  CLEAR_NEW_MESSAGE,
  SET_SCHEDULE_TYPE,
  UPDATE_SCHEDULED_DRAFT,
  SET_SCHEDULED_DRAFT,
  REMOVE_FILE_ERROR,
  REMOVE_FILE,
  CLEAR_FILES,
  SET_PHONE_TYPES,
  CLEAR_PHONE_TYPES,
  SET_RESTORED_PHONE_NUMBERS,
  REMOVE_NUMBER_FROM_RESTORED_PHONE_NUMBERS,
  SET_BROADCAST_SUPPRESSION_TIMEFRAME,
  CLEAR_BROADCAST_SUPPRESSION_TIMEFRAME,
  SET_CURRENTLY_APPLIED_SUPPRESSION,
  CLEAR_RESTORED_PHONE_NUMBERS,
  SET_RESTORED_LANDLINE_NUMBERS,
  SET_GRAB_NUMBERS_SCRAPED_HTML,
  SET_GRAB_NUMBERS_WITH_NAMES,
  SET_URL_STATE,
  MSG_EXT_LOCATION_DETAILS,
} from 'src/action-creators/draft-messages';


export type State = {
  // what if instead we always just have these here
  // there is only ever one "new" id instead of having to
  // rebuild the broadcast from all these parts
  drafts: {
    [phone: string]: MessagingDraft;
  };
  recipients: Array<SuggestionType | string>;
  inputPhone: string | null | undefined;
  nextDraftId: string;
  phoneTypes: {
    [key: string]: BroadcastSuppressionPhoneDetails;
  };
  restoredPhoneNumbers: string[];
  restoredLandlineNumbers: string[];
  timeFrame: TimeFrame;
  candidateDetails: SetOfDiscoverCandidateDetails;
  isDiscoverIframeContext: boolean;
  grabNumbersScrapedHtml: string;
  grabNumbersWithNames: NumbersWithNames[];
  applySuppression: boolean;
  extensionPathDetails: MsgExtLocationDetails;
  grabbedPhoneNumbers: Recipient[];
};

export default (
  state: State = {
    // pseudo-broadcasts keyed by phone number/'new'
    drafts: {},
    // for broadcasting when there's more than one recipient
    recipients: [],
    // whatever is shown in the "To:" input box
    inputPhone: null,
    // the id of the next draft (note that uniqueId is only called when the reducer is created).
    nextDraftId: uniqueId(),
    phoneTypes: {},
    restoredPhoneNumbers: [],
    restoredLandlineNumbers: [],
    timeFrame: {},
    origin: 'messaging',
    candidateDetails: {},
    isDiscoverIframeContext: false,
    grabNumbersScrapedHtml: '',
    grabNumbersWithNames: [],
    applySuppression: true,
    extensionPathDetails: {
      domain: '',
      path: '',
      fullPath: '',
    },
    grabbedPhoneNumbers: [],
  },
  action: Action,
): State => {
  switch (action.type) {
    case MSG_EXT_LOCATION_DETAILS: {
      return {
        ...state,
        extensionPathDetails: action.payload,
      };
    }
    case SET_PHONE_TYPES: {
      const phoneTypes: Record<string, any> = {};
      action.payload.value.forEach((phoneInfo) => {
        phoneTypes[phoneInfo.phoneNumber] = phoneInfo;
      });
      const phoneNumbers = Object.keys(phoneTypes);
      let restoredPhoneNumbers = [];
      if (state.applySuppression) {
        // "Apply" - no recently messaged numbers should be restored
        restoredPhoneNumbers = [];
      } else {
        // "Don't Apply" - all recently messaged numbers should be restored
        const recentlyMessagedNumbers = phoneNumbers.filter(
          (phoneNumber) =>
            phoneTypes[phoneNumber].broadcastSuppression === true,
        );
        restoredPhoneNumbers = recentlyMessagedNumbers;
      }

      return {
        ...state,
        phoneTypes,
        restoredPhoneNumbers,
        restoredLandlineNumbers: state.restoredLandlineNumbers.filter(
          (phoneNumber) => phoneNumbers.includes(phoneNumber),
        ),
      };
    }
    case CLEAR_PHONE_TYPES:
      return {
        ...state,
        phoneTypes: {},
      };
    case SET_RESTORED_PHONE_NUMBERS:
      return {
        ...state,
        restoredPhoneNumbers: [
          ...state.restoredPhoneNumbers,
          ...action.payload.value,
        ],
      };
    case SET_RESTORED_LANDLINE_NUMBERS:
      return {
        ...state,
        restoredLandlineNumbers: [
          ...state.restoredLandlineNumbers,
          ...action.payload.value,
        ],
      };
    case CLEAR_RESTORED_PHONE_NUMBERS:
      return {
        ...state,
        restoredPhoneNumbers: [],
        restoredLandlineNumbers: [],
      };
    case REMOVE_NUMBER_FROM_RESTORED_PHONE_NUMBERS:
      const restoredPhoneNumbers = state.restoredPhoneNumbers.filter(
        (phone) => !action.payload.value.includes(phone),
      );
      const restoredLandlineNumbers = state.restoredLandlineNumbers.filter(
        (phone) => !action.payload.value.includes(phone),
      );
      return {
        ...state,
        restoredPhoneNumbers,
        restoredLandlineNumbers,
      };
    case SET_BROADCAST_SUPPRESSION_TIMEFRAME:
      return {
        ...state,
        timeFrame: action.payload.value,
      };
    case CLEAR_BROADCAST_SUPPRESSION_TIMEFRAME:
      return {
        ...state,
        timeFrame: {},
      };
    case SET_CURRENTLY_APPLIED_SUPPRESSION: {
      const newSuppressionValue = action.payload.value;
      // Get all phone numbers that have broadcastSuppression: true (recently messaged numbers)
      const allPhoneNumbers = Object.values(state.phoneTypes);
      const recentlyMessagedNumbers = allPhoneNumbers
        .filter((phoneInfo: any) => phoneInfo.broadcastSuppression === true)
        .map((phoneInfo: any) => phoneInfo.phoneNumber);

      let restoredPhoneNumbers = [];
      if (newSuppressionValue) {
        // "Apply" - no recently messaged numbers should be restored
        restoredPhoneNumbers = [];
      } else {
        // "Don't Apply" - all recently messaged numbers should be restored
        restoredPhoneNumbers = [
          ...state.restoredPhoneNumbers,
          ...recentlyMessagedNumbers.filter(
            (phoneNumber) => !state.restoredPhoneNumbers.includes(phoneNumber),
          ),
        ];
      }

      return {
        ...state,
        applySuppression: newSuppressionValue,
        restoredPhoneNumbers,
      };
    }
    case SET_GRAB_NUMBERS_SCRAPED_HTML:
      return {...state, grabNumbersScrapedHtml: action.payload.value};
    case SET_GRAB_NUMBERS_WITH_NAMES:
      return {...state, grabNumbersWithNames: action.payload.value};
    case SET_GRABBED_PHONE_NUMBERS:
      return {...state, grabbedPhoneNumbers: action.payload.value};
    case INPUT_PHONE: {
      let inputPhone = action.payload;

      if (inputPhone === undefined) {
        inputPhone = state.inputPhone || '';
      }

      return {
        ...state,
        inputPhone,
      };
    }

    case SET_MULTIPLE_RECIPIENTS: {
      return {
        ...state,
        recipients: action.payload ? action.payload : [],
      };
    }

    case SET_DISCOVER_OR_TRM_ORIGIN_AND_METADATA: {
      return {
        ...state,
        origin: action.payload.origin,
        candidateDetails: action.payload.candidateDetails,
      };
    }
    case SET_DISCOVER_OR_TRM_IFRAME_CONTEXT: {
      return {
        ...state,
        isDiscoverIframeContext: action.payload,
      };
    }

    case CHANGE_DRAFT: {
      const {to, body} = action.payload;
      return reduceDraftUpdate(state, to, {body});
    }

    case ADD_SCHEDULE: {
      return reduceDraftUpdate(state, action.meta.toPhone, {
        sendDate: action.payload,
      });
    }

    case SET_SCHEDULE_TYPE: {
      const {toPhone, scheduleType, sendDate} = action.payload;
      return reduceDraftUpdate(state, toPhone, {
        sends: scheduleType,
        sendDate,
      });
    }

    case UPDATE_SCHEDULED_DRAFT: {
      const {draftKey, draft} = action.payload;
      return reduceDraftUpdate(state, draftKey, draft);
    }
    case SET_SCHEDULED_DRAFT: {
      return {
        ...state,
        drafts: {
          ...state.drafts,
          [action.payload.draftKey]: {
            ...action.payload.draft,
            id: state.nextDraftId,
          },
        },
        nextDraftId: uniqueId(),
      };
    }

    case CLEAR_DRAFT: {
      return {
        ...state,
        drafts: omit(state.drafts, action.payload),
        inputPhone: null,
      };
    }

    case CLEAR_NEW_MESSAGE: {
      return {
        ...state,
        inputPhone: '',
        recipients: [],
        drafts: omit(state.drafts, 'new'),
      };
    }

    case REMOVE_FILE_ERROR: {
      const {draftKey, error} = action.payload;

      const prevErrors = state.drafts[draftKey]?.fileErrors || [];

      const nextErrors = prevErrors.filter((err) => err !== error);

      return reduceDraftUpdate(state, draftKey, {fileErrors: nextErrors});
    }

    case REMOVE_FILE: {
      const {draftKey, file} = action.payload;

      const prevFiles = state.drafts[draftKey]?.files || [];

      const nextFiles = prevFiles.filter((f) => f !== file);

      return reduceDraftUpdate(state, draftKey, {files: nextFiles});
    }

    case CLEAR_FILES: {
      const {draftKey} = action.payload;

      return reduceDraftUpdate(state, draftKey, {files: [], fileErrors: []});
    }

    case SET_URL_STATE: {
      const {draftKey, detectedUrls, bannerVisible, shouldShortenLinks} =
        action.payload;
      return reduceDraftUpdate(state, draftKey, {
        detectedUrls,
        bannerVisible,
        shouldShortenLinks:
          shouldShortenLinks ??
          state.drafts[draftKey]?.shouldShortenLinks ??
          true,
      });
    }
  }

  return state;
};

/**
 * if a draft with the key does not exist,
 * it will create a new one with a unique id.
 */
function reduceDraftUpdate(
  state: State,
  draftKey: string,
  draftUpdate: Partial<MessagingDraft>,
): State {
  let draft = state.drafts[draftKey];

  if (!draft) {
    draft = {id: state.nextDraftId};
    state = {
      ...state,
      nextDraftId: uniqueId(),
    };
  }

  draft = {
    ...draft,
    ...draftUpdate,
  };

  return {
    ...state,
    drafts: {
      ...state.drafts,
      [draftKey]: draft,
    },
  };
}
